import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { MfaService } from '../../../services/mfa.service';

@Component({
  selector: 'app-mfa-verify',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './mfa-verify.component.html',
  styleUrls: ['./mfa-verify.component.scss']
})
export class MfaVerifyComponent implements OnInit, OnDestroy {
  @Input() username = '';
  @Output() verified = new EventEmitter<boolean>();
  @Output() cancel = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  
  verificationForm: FormGroup;
  loading = false;
  error = '';
  attempts = 0;
  maxAttempts = 3;

  constructor(
    private fb: FormBuilder,
    private mfaService: MfaService
  ) {
    this.verificationForm = this.fb.group({
      verificationCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    // Auto-focus on the verification code input
    setTimeout(() => {
      const input = document.querySelector('.verification-input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onVerify() {
    if (this.verificationForm.invalid || !this.username) {
      return;
    }

    const verificationCode = this.verificationForm.get('verificationCode')?.value;
    this.loading = true;
    this.error = '';

    this.mfaService.verify(this.username, verificationCode)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.verified.emit(true);
          } else {
            this.attempts++;
            this.error = response.message || '驗證碼錯誤，請重試';
            this.verificationForm.get('verificationCode')?.setValue('');
            
            if (this.attempts >= this.maxAttempts) {
              this.error = '驗證失敗次數過多，請稍後再試';
              setTimeout(() => {
                this.cancel.emit();
              }, 2000);
            }
          }
        },
        error: (error) => {
          console.error('MFA 驗證錯誤:', error);
          this.attempts++;
          this.error = '驗證過程中發生錯誤，請稍後再試';
          
          if (this.attempts >= this.maxAttempts) {
            setTimeout(() => {
              this.cancel.emit();
            }, 2000);
          }
        }
      });
  }

  onCancel() {
    this.cancel.emit();
  }

  onInputChange() {
    // Clear error when user starts typing
    if (this.error) {
      this.error = '';
    }
  }

  get remainingAttempts() {
    return Math.max(0, this.maxAttempts - this.attempts);
  }

  get showAttemptWarning() {
    return this.attempts > 0 && this.attempts < this.maxAttempts;
  }

  get isBlocked() {
    return this.attempts >= this.maxAttempts;
  }
}